import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DailyBalanceChart,
  DailyTradeChart,
} from '@features/ui';
import { BalanceStackedData, StackedData } from '@src/types';
import {
  getDailyProfitLossAndBalance,
  getDailyTradeVolumesAndContracts,
} from '@src/services/payout.ts';

interface AccountChartsDialogProps {
  open: boolean;
  accountId: number | null;
  accountName?: string;
  onOpenChange: (open: boolean) => void;
}

export const AccountChartsDialog: FC<AccountChartsDialogProps> = ({
  open,
  accountId,
  accountName,
  onOpenChange,
}) => {
  const [tradeData, setTradeData] = useState<StackedData[]>([]);
  const [balanceData, setBalanceData] = useState<BalanceStackedData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !accountId) {
      setTradeData([]);
      setBalanceData([]);

      return;
    }

    const fetchChartData = async () => {
      setLoading(true);
      try {
        const [res1, res2] = await Promise.all([
          getDailyTradeVolumesAndContracts(accountId),
          getDailyProfitLossAndBalance(accountId),
        ]);

        setTradeData(res1.data);
        setBalanceData(res2.data);
      } catch (error) {
        console.error('Failed to fetch chart data:', error);
        setTradeData([]);
        setBalanceData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [open, accountId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[980px] w-[98%] max-h-[90vh] overflow-y-auto"
        size="xl"
      >
        <DialogHeader>
          <DialogTitle>{`Account ${accountName ?? ''}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-8">
          <div>
            <p className="text-center font-semibold mb-2">
              Daily Trade Volume vs Contracts
            </p>
            {loading ? (
              <div className="h-[320px] flex items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : (
              <DailyTradeChart data={tradeData} height={320} />
            )}
          </div>
          <div>
            <p className="text-center font-semibold mb-2">
              Daily Net Profit / Loss vs Account Balance
            </p>
            {loading ? (
              <div className="h-[320px] flex items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : (
              <DailyBalanceChart data={balanceData} height={320} />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
